import type { FC } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import type { UseFormSetValue } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import { useControlledFieldArray } from '@/hooks/react-hook-form/useControlledFieldArray';
import type { OmitSafely } from '@/types';

import type { CostEstimationItemsGroupedTableProps } from '../../CostEstimationItemsGroupedTables';
import CostEstimationItemsGroupedTables from '../../CostEstimationItemsGroupedTables';
import { mapFromCostEstimationItemsTableRecord } from '../../mappers/estimationItemsEditFormModelMapper';
import type {
  CostEstimationItemsEditFormModel,
  CostEstimationItemsFormModelRow,
  CostEstimationItemsTableRecord,
  EstimationItemActionCallback,
  EstimationItemIsIncludedActionCallback,
  EstimationItemIsIncludedActionPromiseCallback,
} from '../../model';

interface TopLevelEstimationItemsFormProps {
  onSubmit: (
    formModel: CostEstimationItemsEditFormModel,
    removeEstimationItemFromModelById: EstimationItemActionCallback,
  ) => Promise<void>;
  keepDirtyValues: boolean;
  topLevelEstimationItems: CostEstimationItemsTableRecord[];
  tableProps: OmitSafely<
    CostEstimationItemsGroupedTableProps,
    'estimationItems' | 'control' | 'onIncludeClicked' | 'onCostFieldBlur'
  >;
}

export const TopLevelEstimationItemsForm: FC<TopLevelEstimationItemsFormProps> = ({
  onSubmit,
  keepDirtyValues,
  topLevelEstimationItems,
  tableProps,
}) => {
  const formModelInitial = useMemo<CostEstimationItemsEditFormModel>(
    () => ({
      estimationItems: topLevelEstimationItems.map((x) =>
        mapFromCostEstimationItemsTableRecord(x, tableProps.estimationModel),
      ),
    }),
    [topLevelEstimationItems, tableProps.estimationModel],
  );

  const { control, handleSubmit, reset, watch, setValue } =
    useForm<CostEstimationItemsEditFormModel>({
      mode: 'onChange',
      defaultValues: formModelInitial,
    });

  const { remove, fields } = useControlledFieldArray({ control, name: 'estimationItems' });

  const resetDirtyFieldsState = useCallback(() => {
    reset(undefined, { keepValues: true }); // Retain current values but clear dirty state
  }, [reset]);

  const removeEstimationItemFromModelById = useCallback<EstimationItemActionCallback>(
    (estimationItemId: string) => {
      /**
       * Workaround:
       * There's an issue when removing item from collection when last item has a field with dirty state and form reset with keepDirtyValues:true is called.
       * It then tries to keep value of an item that is no longer maintained in model. As a result, it appends some inconsistent partial field at the end of collection.
       * In order to prevent it from happening, when removing item we firstly simply reset dirty fields state so that when fresh data are fetched after form submit, all the fields are reset (because there's no dirty one anymore).
       */
      resetDirtyFieldsState();
      return remove(fields.findIndex((field) => field.id === estimationItemId));
    },
    [remove, fields, resetDirtyFieldsState],
  );

  const enrichedOnSubmit = useCallback(
    async (formModel: CostEstimationItemsEditFormModel) => {
      await onSubmit(formModel, removeEstimationItemFromModelById);
    },
    [onSubmit, removeEstimationItemFromModelById],
  );

  useEffect(() => {
    reset(formModelInitial, { keepDirtyValues });
  }, [formModelInitial, keepDirtyValues, reset]);

  const onIncludeClicked: EstimationItemIsIncludedActionPromiseCallback = async (
    estimationItemId,
    isIncluded,
  ) => {
    // excluding or deleting
    if (!isIncluded) {
      const estimationItem = topLevelEstimationItems.find((x) => x.id === estimationItemId);
      if (!estimationItem) {
        throw new Error('Estimation item not found.');
      }

      if (!estimationItem.estimationModelItemId) {
        setDelete(watch('estimationItems'), estimationItem.id, setValue);
        return Promise.resolve();
      }

      // omit straight away
      setOmit(watch('estimationItems'), estimationItemId, setValue);
      return Promise.resolve();
    }
    // including
    return Promise.resolve();
  };

  const onIncludeChanged: EstimationItemIsIncludedActionCallback = async () => {
    await handleSubmit(enrichedOnSubmit)();
  };

  /**
   * The collection of estimation items from BE is normally matched to form model and we reset the form whenever the collection changes.
   * However the reset function is asynchronous and the change isn't immediate. In order to prevent issues when it cannot find form data
   * for a table row, we display only those estimation items that are in the form model.
   */
  const tableItems = topLevelEstimationItems.filter((x) =>
    fields.some((formModelItem) => formModelItem.id === x.id),
  );

  return (
    <form onSubmit={handleSubmit(enrichedOnSubmit)}>
      <CostEstimationItemsGroupedTables
        control={control}
        estimationItems={tableItems}
        onCostFieldBlur={handleSubmit(enrichedOnSubmit)}
        {...tableProps}
        onIncludeClicked={onIncludeClicked}
        onIncludeChanged={onIncludeChanged}
      />
      {/* The submit button is here, but just hidden so that we can submit form by pressing enter */}

      <input type='submit' className='hidden' />
    </form>
  );
};

function setOmit(
  formModelEstimationItems: CostEstimationItemsFormModelRow[],
  estimationItemId: string | undefined,
  setValue: UseFormSetValue<CostEstimationItemsEditFormModel>,
) {
  const rowIndex = formModelEstimationItems.findIndex((x) => x.id === estimationItemId);
  setValue(`estimationItems.${rowIndex}.isIncluded`, false);
}

function setDelete(
  formModelEstimationItems: CostEstimationItemsFormModelRow[],
  estimationItemId: string | undefined,
  setValue: UseFormSetValue<CostEstimationItemsEditFormModel>,
) {
  const rowIndex = formModelEstimationItems.findIndex((x) => x.id === estimationItemId);
  setValue(`estimationItems.${rowIndex}.shouldDelete`, true);
  setValue(`estimationItems.${rowIndex}.isIncluded`, false);
}
